import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {UnsubscribeOnDestroyAdapter} from '@shared';
import {DefaultResponse} from "@core/models/Http/DefaultResponse";
import {environment} from "@enviroment/environment";
import {GeneralFunctionsService} from "@core/service/generalFunctions.service";
import {Patient} from "@core/models/Patient";
import {PatientByIdMedic} from "@core/models/PatientByIdMedic";
import {HttpMesageHandlerService} from "@core/service/http-mesage-handler";
import {MsgResponse} from "@core/models/MsgResponse";
import {Tutor} from "@core/models/Tutor";

@Injectable({
  providedIn: 'root',
})

export class PatientService extends UnsubscribeOnDestroyAdapter {
  isTblLoading = true;
  dataChange: BehaviorSubject<Patient[]> = new BehaviorSubject<Patient[]>([]);

  constructor(
    private httpClient: HttpClient,
    private readonly gfs: GeneralFunctionsService,
    private httpMsgHandler: HttpMesageHandlerService
  ) {
    super();
  }

  get data(): Patient[] {
    return this.dataChange.value;
  }

  /** CRUD METHODS */
  /**
   * Obtiene todos los pacientes por GEMP
   */
  getAllPatientsByGemp(idGemp: string): void {
    this.isTblLoading = true;

    if (!idGemp) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    const params = new HttpParams().set('IdGEMP', idGemp);

    this.subs.sink = this.httpClient.get<DefaultResponse<Patient[]>>(
      `${environment.backend_url}Pacientes`,
      { params }
    ).subscribe({
      next: (data: DefaultResponse<Patient[]>): void => {
        this.isTblLoading = false;
        this.processPatientData(data.data);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  /**
   * Obtiene todos los pacientes por Usuario
   */
  getAllPatientsByUsuario(idUsuario: string): void {
    this.isTblLoading = true;

    if (!idUsuario) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    const params = new HttpParams().set('IdUsuario', idUsuario);

    this.subs.sink = this.httpClient.get<DefaultResponse<Patient[]>>(
      `${environment.backend_url}Pacientes`,
      { params }
    ).subscribe({
      next: (data: DefaultResponse<Patient[]>): void => {
        this.isTblLoading = false;
        this.processPatientData(data.data);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  /**
   * Obtiene un paciente especifico por ID
   */
  getPatientById(idPaciente: string): void {
    this.isTblLoading = true;

    if (!idPaciente) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    const params = new HttpParams().set('IdPaciente', idPaciente);

    this.subs.sink = this.httpClient.get<DefaultResponse<Patient[]>>(
      `${environment.backend_url}Pacientes`,
      { params }
    ).subscribe({
      next: (data: DefaultResponse<Patient[]>): void => {
        this.isTblLoading = false;
        this.processPatientData(data.data);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  /**
   * Obtiene todos los pacientes por Sucursal
   */
  getAllPatientsBySucursal(idSucursal: string): void {
    this.isTblLoading = true;

    if (!idSucursal) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    const params = new HttpParams().set('IdSucursal', idSucursal);

    this.subs.sink = this.httpClient.get<DefaultResponse<Patient[]>>(
      `${environment.backend_url}Pacientes`,
      { params }
    ).subscribe({
      next: (data: DefaultResponse<Patient[]>): void => {
        this.isTblLoading = false;
        this.processPatientData(data.data);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  /**
   * Obtiene todos los pacientes por Medico
   */
  getAllPatientsByMedico(idMedico: string): void {
    this.isTblLoading = true;

    if (!idMedico) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    const params = new HttpParams().set('IdMedico', idMedico);

    this.subs.sink = this.httpClient.get<DefaultResponse<Patient[]>>(
      `${environment.backend_url}Pacientes`,
      { params }
    ).subscribe({
      next: (data: DefaultResponse<Patient[]>): void => {
        this.isTblLoading = false;
        this.processPatientData(data.data);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  /**
   * Busca pacientes por nombre
   */
  searchPatientsByName(nombreBusqueda: string): void {
    this.isTblLoading = true;

    if (!nombreBusqueda || nombreBusqueda.trim() === '') {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    const params = new HttpParams().set('NombreBusqueda', nombreBusqueda);

    this.subs.sink = this.httpClient.get<DefaultResponse<Patient[]>>(
      `${environment.backend_url}Pacientes`,
      { params }
    ).subscribe({
      next: (data: DefaultResponse<Patient[]>): void => {
        this.isTblLoading = false;
        this.processPatientData(data.data);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  getAllPatientsbyIdBranch(idBranch: string): void {
    this.isTblLoading = true;

    this.subs.sink = this.httpClient.get<DefaultResponse<PatientByIdMedic[]>>(
      `${environment.backend_url}PacienteConsulta/BySucursal/${idBranch}`
    ).subscribe({
      next: (data: DefaultResponse<PatientByIdMedic[]>): void => {
        this.isTblLoading = false;

        const combinado = data.data.map(obj => ({
          ...obj,
          status: 'Activo',
          nombres: `${obj.nombres} ${obj.primerApellido} ${obj.segundoApellido}`
        }));
        this.dataChange.next(combinado);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  /**
   * Obtiene pacientes con multiples filtros
   */
  searchPatientsWithFilters(filters: {
    idUsuario?: string;
    idPaciente?: string;
    idSucursal?: string;
    idGemp?: string;
    idMedico?: string;
    nombreBusqueda?: string;
    soloDatosPaciente?: boolean;
  }): void {
    this.isTblLoading = true;

    let params = new HttpParams();

    if (filters.idUsuario) {
      params = params.set('IdUsuario', filters.idUsuario);
    }
    if (filters.idPaciente) {
      params = params.set('IdPaciente', filters.idPaciente);
    }
    if (filters.idSucursal) {
      params = params.set('IdSucursal', filters.idSucursal);
    }
    if (filters.idGemp) {
      params = params.set('IdGEMP', filters.idGemp);
    }
    if (filters.idMedico) {
      params = params.set('IdMedico', filters.idMedico);
    }
    if (filters.nombreBusqueda) {
      params = params.set('NombreBusqueda', filters.nombreBusqueda);
    }
    if (filters.soloDatosPaciente !== undefined) {
      params = params.set('SoloDatosPaciente', filters.soloDatosPaciente.toString());
    }

    this.subs.sink = this.httpClient.get<DefaultResponse<Patient[]>>(
      `${environment.backend_url}Pacientes`,
      { params }
    ).subscribe({
      next: (data: DefaultResponse<Patient[]>): void => {
        this.isTblLoading = false;
        this.processPatientData(data.data);
      },
      error: (error: HttpErrorResponse): void => {
        this.isTblLoading = false;
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  /**
   * Metodo auxiliar para procesar los datos de pacientes
   * Evita duplicacion de codigo
   */
  private processPatientData(patients: Patient[]): void {
    const combinado = patients.map(obj => ({
      ...obj,
      status: 'Activo',
      nombres: `${obj.nombres} ${obj.primerApellido} ${obj.segundoApellido}`
    }));
    this.dataChange.next(combinado);
  }

  /**
   * Crea o actualiza un paciente usando el endpoint upsert
   * @param data - Datos del paciente a crear o actualizar
   * @returns Observable con la respuesta del servidor
   */
  createUpdatePatient(data: Patient): Observable<DefaultResponse<string>> {
    const cleanData = this.gfs.mayusAcentos(data);
    const url = `${environment.backend_url}Pacientes/upsert`;

    return this.httpClient.post<DefaultResponse<string>>(url, cleanData);
  }

  deletePatient(id: string): void {
    this.httpClient.delete(`${environment.backend_url}Pacientes/${id}`)
      .subscribe({
        next: (res) => {
          this.httpMsgHandler.handleMsg(res as MsgResponse);
        },
        error: (error: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(error);
        },
      });
  }

  createUpdateTutor(data: Tutor) {
    const cleanData = this.gfs.mayusAcentos(data);
    const url = `${environment.backend_url}Tutores`;
    if (data.idTutor) {
      return this.httpClient.put<DefaultResponse<string>>(url + '/' + data.idTutor, cleanData);
    } else {
      return this.httpClient.post<DefaultResponse<string>>(url, cleanData);
    }
  }

  getTutorByIdPaciente(idPaciente: string) {
    return this.httpClient.get<DefaultResponse<Tutor>>(
      `${environment.backend_url}Tutores/PorPaciente/${idPaciente}`
    );
  }

  setPinPaciente(data: { pin: string }) {
    return this.httpClient.post<DefaultResponse<any>>(
      `${environment.backend_url}PinPaciente`,
      data
    );
  }

  validatePinPaciente(payload: { pin: string }) {
    return this.httpClient.post(`${environment.backend_url}PinPaciente/Validate`, payload);
  }

  forgotPinPaciente() {
    return this.httpClient.post<DefaultResponse<any>>(
      `${environment.backend_url}PinPaciente/ForgotPin`,
      {}
    );
  }

  setPinPacienteWithBearerToken(
    data: { pin: string },
    tokenFromLink: string
  ) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${tokenFromLink}`,
    });

    return this.httpClient.post(
      `${environment.backend_url}PinPaciente`,
      data,
      { headers }
    );
  }

}
